/**
 * Trial Tenants Management Page
 * Platform admin page for managing trial accounts
 */
import React, { useState } from 'react';
import {
  Title1,
  Body1,
  Body2,
  Card,
  CardHeader,
  Button,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  tokens,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Field,
} from '@fluentui/react-components';
import {
  ClockRegular,
  AddRegular,
  MailRegular,
  PhoneRegular,
  ArrowTrendingRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  BuildingRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trialsApi } from '../services/api';
import type { TrialTenantSummary, CreateTrialTenantRequest } from '../types';

const EMPTY_CREATE_FORM: CreateTrialTenantRequest = {
  companyName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  trialDurationDays: 14,
};

export const TrialsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTrialTenantRequest>(EMPTY_CREATE_FORM);

  const {
    data: trialsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['trials'],
    queryFn: () => trialsApi.getAll(true),
  });

  const createMutation = useMutation({
    mutationFn: (request: CreateTrialTenantRequest) => trialsApi.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trials'] });
      setIsCreateDialogOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
    },
  });

  const handleCreate = () => {
    if (createForm.companyName && createForm.contactName && createForm.contactEmail) {
      createMutation.mutate(createForm);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner size="large" label="Loading trials..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card style={{ margin: '1rem', padding: '1rem' }}>
        <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>
          Error loading trials: {error instanceof Error ? error.message : 'Unknown error'}
        </Body1>
      </Card>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ClockRegular style={{ fontSize: '2rem' }} />
          <Title1>Trial Management</Title1>
        </div>
        <Button appearance="primary" icon={<AddRegular />} onClick={() => setIsCreateDialogOpen(true)}>
          Create Trial
        </Button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <Card>
          <CardHeader
            header={<Body2>Total Trials</Body2>}
            description={<Title1>{trialsData?.totalTrials ?? 0}</Title1>}
          />
        </Card>
        <Card>
          <CardHeader
            header={<Body2>Expiring Soon (7 days)</Body2>}
            description={
              <Title1 style={{ color: tokens.colorPaletteYellowForeground1 }}>
                {trialsData?.expiringIn7Days ?? 0}
              </Title1>
            }
          />
        </Card>
        <Card>
          <CardHeader
            header={<Body2>Expired</Body2>}
            description={
              <Title1 style={{ color: tokens.colorPaletteRedForeground1 }}>
                {trialsData?.expiredCount ?? 0}
              </Title1>
            }
          />
        </Card>
      </div>

      {trialsData?.expiringIn7Days && trialsData.expiringIn7Days > 0 && (
        <MessageBar intent="warning" style={{ marginBottom: '1rem' }}>
          <MessageBarBody>
            {trialsData.expiringIn7Days} trial(s) expiring within 7 days. Consider reaching out for conversion.
          </MessageBarBody>
        </MessageBar>
      )}

      <Card>
        <CardHeader
          header={<Body1 style={{ fontWeight: 600 }}>All Trials</Body1>}
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Company</TableHeaderCell>
              <TableHeaderCell>Contact</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Days Remaining</TableHeaderCell>
              <TableHeaderCell>Expires</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trialsData?.trials.map((trial: TrialTenantSummary) => (
              <TableRow key={trial.tenantId}>
                <TableCell>
                  <span style={{ fontWeight: 500 }}>{trial.companyName}</span>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span>{trial.contactName}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', color: tokens.colorNeutralForeground3 }}>
                      <MailRegular fontSize={12} />
                      {trial.contactEmail}
                    </div>
                    {trial.contactPhone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', color: tokens.colorNeutralForeground3 }}>
                        <PhoneRegular fontSize={12} />
                        {trial.contactPhone}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {trial.isExpired ? (
                    <Badge appearance="filled" color="danger" icon={<DismissCircleRegular />}>
                      Expired
                    </Badge>
                  ) : trial.daysRemaining <= 7 ? (
                    <Badge appearance="filled" color="warning" icon={<ClockRegular />}>
                      Expiring Soon
                    </Badge>
                  ) : (
                    <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
                      Active
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <span style={{
                    fontWeight: 600,
                    color: trial.isExpired
                      ? tokens.colorPaletteRedForeground1
                      : trial.daysRemaining <= 7
                        ? tokens.colorPaletteYellowForeground1
                        : tokens.colorNeutralForeground1
                  }}>
                    {trial.isExpired ? 'Expired' : `${trial.daysRemaining} days`}
                  </span>
                </TableCell>
                <TableCell>
                  {new Date(trial.trialExpiresAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button size="small" icon={<ClockRegular />}>
                      Extend
                    </Button>
                    <Button size="small" appearance="primary" icon={<ArrowTrendingRegular />}>
                      Convert
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {(!trialsData?.trials || trialsData.trials.length === 0) && (
              <TableRow>
                <TableCell colSpan={6}>
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Body1>No trials found</Body1>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Trial Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(_, data) => setIsCreateDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Create Trial Tenant</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
                {createMutation.isError && (
                  <MessageBar intent="error">
                    <MessageBarBody>
                      {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create trial'}
                    </MessageBarBody>
                  </MessageBar>
                )}
                <Field label="Company Name" required>
                  <Input
                    value={createForm.companyName}
                    onChange={(_, data) => setCreateForm({ ...createForm, companyName: data.value })}
                    placeholder="Acme Corporation"
                    contentBefore={<BuildingRegular />}
                  />
                </Field>
                <Field label="Contact Name" required>
                  <Input
                    value={createForm.contactName}
                    onChange={(_, data) => setCreateForm({ ...createForm, contactName: data.value })}
                    placeholder="Jane Smith"
                    contentBefore={<PersonRegular />}
                  />
                </Field>
                <Field label="Contact Email" required>
                  <Input
                    type="email"
                    value={createForm.contactEmail}
                    onChange={(_, data) => setCreateForm({ ...createForm, contactEmail: data.value })}
                    placeholder="jane.smith@acme.com"
                    contentBefore={<MailRegular />}
                  />
                </Field>
                <Field label="Contact Phone" hint="Optional">
                  <Input
                    type="tel"
                    value={createForm.contactPhone ?? ''}
                    onChange={(_, data) => setCreateForm({ ...createForm, contactPhone: data.value })}
                    placeholder="+61 400 000 000"
                    contentBefore={<PhoneRegular />}
                  />
                </Field>
                <Field label="Trial Duration (Days)" hint="Default 14, maximum 30">
                  <Input
                    type="number"
                    value={String(createForm.trialDurationDays ?? 14)}
                    onChange={(_, data) =>
                      setCreateForm({ ...createForm, trialDurationDays: Number(data.value) || 14 })
                    }
                    contentBefore={<ClockRegular />}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleCreate}
                disabled={
                  !createForm.companyName ||
                  !createForm.contactName ||
                  !createForm.contactEmail ||
                  createMutation.isPending
                }
              >
                {createMutation.isPending ? 'Creating...' : 'Create Trial'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
